import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../shared/hook";
import { addOrganisationProductGroups, downloadUserFiles, generateProductGroupDocs, getOrganisationProductGroups, getProductGroups, userFiles, viewAndPrintDocumentHead } from "../../common/api/authApi";
// @ts-ignore
import { NCALayerClient } from "ncalayer-js-client";
import CheckBox from "../../shared/Ui/CheckBox/CheckBox";
import { format } from "date-fns";
import { FaInfoCircle } from "react-icons/fa";
// import {styles} from "./ProfileCard.module.css"
import style from "./ProfileCard.module.css";
import { normalizedLocale } from "../../common/libs/normalizedLocale";
import {
  filterDocumentsByLocale,
  getProductGroupDisplayName,
  localizeProductGroupFileName,
} from "../../common/libs/productGroupLocale";
import { SkeletonTheme } from "react-loading-skeleton";
import MockSkeleton from "../OrderList/utils/MockSkeleton";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import { useNavigate } from "react-router-dom";
import { /* getBalance, */ getClientData } from "../../common/api/asrApi";
import { getMyActiveAccounts, MyActiveAccount } from "../../common/api/billingApi";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import Loader from "../../shared/Ui/Loader/Loader";
import ProductGroupSignModal from "../../shared/Ui/ProductGroupSignModal/ProductGroupSignModal";


function ProfileCard() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<any>({
    values: [],
    totalItems: 0,
    totalPages: 0,
  });
  const navigate = useNavigate();
  const [errorModal, setErrorModal] = useState<any>(null);
  const [initialValues, setInitialValues] = useState<any>({
    balance: '',
    customerId: '',
    iinBin: '',
    nameRu: '',
    nameKk: '',
    providerId: 0,
    createdAt: null,
    updatedAt: null,
    error: '',
    agreements: [
      {
        agreementId: 0,
        number: '',
        createdAt: null,
        updatedAt: null,
        error: '',
        accounts: [
          {
            accountId: 0,
            createdAt: null,
            updatedAt: null,
            error: ''
          }
        ]
      }
    ]
  });
  const [page, setPage] = useState<number>(1);
  const [groups, setGroups] = useState<any[]>([]);
  const [checked, setChecked] = useState<boolean[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [userFilesData, setUserFilesData] = useState([]);
  const [pgModalFiles, setPgModalFiles] = useState<any[] | null>(null);
  const [pgModalGroup, setPgModalGroup] = useState<any>(null);
  const [pgChecked, setPgChecked] = useState(false);
  const [pgSubmitting, setPgSubmitting] = useState(false);
  const [activeAccounts, setActiveAccounts] = useState<MyActiveAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [refreshingAccountId, setRefreshingAccountId] = useState<number | null>(null);
  const userData: any = useAppSelector((state) => state.auth.userData);
  const locale = normalizedLocale(i18n.language);
  type PaginationState = {
    pageIndex: number;
    pageSize: number;
  };
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);
  const [pagination, setPagination] = useState<PaginationState>(() => {
    const savedPagination = localStorage.getItem('paginationProfile');
    return savedPagination ? JSON.parse(savedPagination) : { pageIndex: 0, pageSize: pageSize };
  });

  useEffect(() => {
    onLoad();
    localStorage.setItem('paginationProfile', JSON.stringify(pagination));
  }, [pagination, page, i18n.language]);

  useEffect(() => {
    if (page === 2) {
      loadActiveAccounts();
    }
  }, [page, i18n.language]);

  const loadActiveAccounts = async () => {
    setAccountsLoading(true);
    try {
      const data = await getMyActiveAccounts();
      setActiveAccounts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Ошибка при загрузке активных лицевых счетов", e);
      setActiveAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  // Обновление баланса по конкретной товарной группе (лицевому счету)
  const handleRefreshAccount = async (accountId: number) => {
    setRefreshingAccountId(accountId);
    try {
      const data = await getMyActiveAccounts();
      const updated = Array.isArray(data)
        ? data.find((acc) => acc.accountId === accountId)
        : undefined;
      if (updated) {
        setActiveAccounts((prev) =>
          prev.map((acc) => (acc.accountId === accountId ? updated : acc)),
        );
      }
    } catch (e) {
      console.error("Ошибка при обновлении баланса лицевого счета", e);
    } finally {
      setRefreshingAccountId(null);
    }
  };

  const formatBalance = (value: number) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "-";
    return new Intl.NumberFormat(locale === "kk" ? "kk-KZ" : "ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Открытие модалки подписи: генерируем документы для выбранной товарной группы
  const handleSubscribeClick = async (group: any) => {
    setPgModalGroup(group);
    setPgChecked(false);
    setIsLoading(true);
    try {
      const response = await generateProductGroupDocs([group.id]);
      const files = Array.isArray(response) ? response : response?.data ?? [];
      setPgModalFiles(files);
    } catch (e: any) {
      const msg = e?.response?.data?.message || t("productGroupSign.generateError");
      setErrorModal(msg);
      setPgModalGroup(null);
    } finally {
      setIsLoading(false);
    }
  };

  const closePgModal = () => {
    setPgModalFiles(null);
    setPgModalGroup(null);
    setPgChecked(false);
  };

  const getGroupDocs = (groupCode: string) => {
    const all: any[] = Array.isArray(userFilesData) ? userFilesData : [];
    return filterDocumentsByLocale(
      all.filter(
        (f: any) => f.productGroupCode === groupCode && f.fileName,
      ),
      locale,
    );
  };

  // Открыть файл из /users/files в новой вкладке
  const handleOpenGroupDoc = async (guidMinio: string) => {
    try {
      const fileBlob = await downloadUserFiles(guidMinio);
      const pdfBlob =
        fileBlob.type === "application/pdf"
          ? fileBlob
          : new Blob([fileBlob], { type: "application/pdf" });
      const fileUrl = window.URL.createObjectURL(pdfBlob);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000);
    } catch (error) {
      console.error("Ошибка при открытии файла:", error);
    }
  };

  // Открытие временного файла договора/заявления в новой вкладке
  const handleDownloadPgFile = async (filename: string, name: string) => {
    try {
      setIsLoading(true);
      const fileBlob = await viewAndPrintDocumentHead(filename);
      const pdfBlob =
        fileBlob.type === "application/pdf"
          ? fileBlob
          : new Blob([fileBlob], { type: "application/pdf" });
      const fileUrl = window.URL.createObjectURL(pdfBlob);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000);
    } catch (error) {
      console.error("Ошибка при открытии файла:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Подпись документов (CMS) и подключение товарной группы
  const handleSignAndConnect = async () => {
    if (!pgModalFiles || !pgModalGroup) return;
    setPgSubmitting(true);

    const ncalayerClient = new NCALayerClient();
    try {
      await ncalayerClient.connect();
    } catch (error) {
      setErrorModal(t("productGroupSign.ncalayerError"));
      setPgSubmitting(false);
      return;
    }

    // В UI показываем только 2 дока на текущем языке, в подпись уходят все 4
    const cmsData = pgModalFiles
      .map((file: any) => file?.sha256Hash)
      .filter(Boolean)
      .join(",");

    let signature: any;
    try {
      signature = await ncalayerClient.basicsSignCMS(
        NCALayerClient.basicsStoragesAll,
        cmsData,
        NCALayerClient.basicsCMSParams,
        process.env.REACT_APP_ETRACK_TEST === "true"
          ? NCALayerClient.basicsSignerTestAny
          : NCALayerClient.basicsSignerAny,
        "ru",
      );
    } catch (error) {
      console.error("Ошибка при выполнении CMS-подписи:", error);
      setPgSubmitting(false);
      return;
    }

    if (Array.isArray(signature)) {
      signature = signature[0];
    }
    signature = signature
      .replace("-----BEGIN CMS-----", "")
      .replace("-----END CMS-----", "")
      .trim();

    try {
      await addOrganisationProductGroups([pgModalGroup.id], signature);
      closePgModal();
      await onLoad();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || t("productGroupSign.connectError");
      setErrorModal(msg);
    } finally {
      setPgSubmitting(false);
    }
  };

  const onLoad = async () => {
    setIsLoading(true);
    try {
      const updateDate = format(new Date(), "dd.MM.yyyy, HH:mm:ss");

      const [userFilesRes, clientRes] = await Promise.allSettled([
        userFiles(),
        // getBalance(), // временно отключено: /asr/get-balance
        getClientData(),
      ]);

      if (userFilesRes.status === "fulfilled") {
        setUserFilesData(userFilesRes.value.data);
      } else {
        console.error("Error fetching user files:", userFilesRes.reason);
      }

      let balance: any = null;
      // временно отключено: /asr/get-balance
      // if (balanceRes.status === "fulfilled") {
      //   balance = balanceRes.value;
      // } else {
      //   console.log("error", balanceRes.reason);
      //   const msg = (balanceRes.reason as any)?.response?.data?.message;
      //   if (msg) setErrorModal(msg);
      // }

      let orderResponse: any = null;
      if (clientRes.status === "fulfilled") {
        orderResponse = clientRes.value;
      } else {
        console.error("Ошибка при получении данных клиента:", clientRes.reason);
      }
      if (orderResponse?.error) setErrorModal(orderResponse.error);

      if (page === 4) {
        const [allGroupsRes, activeGroupsRes] = await Promise.all([
          getProductGroups(),
          getOrganisationProductGroups(),
        ]);

        setGroups(allGroupsRes);

        const nextChecked = allGroupsRes.map((pg: any) =>
          activeGroupsRes.some((ag: any) => ag.productGroupId === pg.id),
        );
        setChecked(nextChecked);

        setSelectedIds(
          allGroupsRes
            .filter((pg: any, idx: number) => nextChecked[idx])
            .map((pg: any) => pg.id),
        );
      }

      setInitialValues({
        ...(orderResponse || {}),
        balance,
        updateDate,
      });
    } catch (e) {
      console.error("Ошибка при загрузке данных:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateItemNumber = (index: any) => {
    return pagination.pageIndex * pagination.pageSize + index + 1;
  };

  interface StatusType {
    type: string;
    label: string;
  }
  const statusTypes: { [key: string]: StatusType } = {
    DRAFT: { type: "test", label: t("documentStatus.draft") },
    ERROR: { type: "error", label: t("documentStatus.error") },
    IN_PROCESSING: { type: "test", label: t("documentStatus.in_processing") },
    PROCESSED: { type: "active", label: t("documentStatus.processed") },
    IN_ACCEPTANCE: { type: "test", label: t("documentStatus.in_acceptance") },
    IN_CONFIRMATION: {
      type: "test",
      label: t("documentStatus.in_confirmation"),
    },
    WITHDRAWN: { type: "error", label: t("documentStatus.withdrawn") },
    REJECTED: { type: "error", label: t("documentStatus.rejected") },
    SIGNED_BY_RECIPIENT: {
      type: "active",
      label: t("documentStatus.signed_by_recipient"),
    },
    NOT_PROCESSED: { type: "error", label: t("documentStatus.not_processed") }
  };

  const hasRoleOperator = Array.isArray(userData.selectRoleV2) && userData.selectRoleV2.some((item: any) => item.roleId === 5);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => { }}
      enableReinitialize={true}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {(formik) => (
        <div className="w-full flex flex-col gap-[40px] font-['Inter']">
          <div className="flex flex-row gap-[15px] cursor-pointer text-[17px]">
            <p className={`cursor-pointer relative ${page === 1 ? 'text-green after:content-[""] after:absolute after:left-0 after:bottom-[-2px] after:w-full after:h-[2px] after:bg-green' : ''}`} onClick={() => setPage(1)}>{t('accountDetails.organizationInfo')}</p>
            <p className={`cursor-pointer relative ${page === 2 ? 'text-green after:content-[""] after:absolute after:left-0 after:bottom-[-2px] after:w-full after:h-[2px] after:bg-green' : ''}`} onClick={() => setPage(2)}>{t('accountDetails.accountNumber')}</p>
            <p className={`cursor-pointer relative ${page === 4 ? 'text-green after:content-[""] after:absolute after:left-0 after:bottom-[-2px] after:w-full after:h-[2px] after:bg-green' : ''}`} onClick={() => setPage(4)}>{t('accountDetails.tradingGroup')}</p>
            {/* <p className={`cursor-pointer relative ${page === 3 ? 'text-green after:content-[""] after:absolute after:left-0 after:bottom-[-2px] after:w-full after:h-[2px] after:bg-green' : ''}`} onClick={() => setPage(3)}>{t('accountDetails.organizationEmployees')}</p> */}
          </div>
          {page == 1 && (
            <>
              <div className="w-full flex flex-col bg-white rounded-3xl p-[30px] gap-[30px] max-w-[1200px]">
                <div className="flex items-center text-base text-gray-600 font-light gap-x-[10px]">
                  <img src="assets/contactData.png" className="w-[20px]" />
                  <div className="uppercase">{t("profile_card.contacts")}</div>
                </div>
                <div className="flex gap-x-[100px] gap-y-[20px] items-center text-base flex-wrap">
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.name")}
                    <p className="font-light">
                      {userData.userName} {userData.userSurname}{" "}
                      {userData.userPatronymic}
                    </p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.iin")}
                    <p className=" font-light">{userData.userIIN}</p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.phone")}
                    <p className="text-gray-600 font-light">{userData.phone}</p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.email")}
                    <p className="text-gray-600 font-light">{userData.email}</p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.postion")}
                    <p className="text-gray-600 font-light">
                      {locale == "ru" ? userData.userPositionRu : userData.userPositionKz}


                    </p>
                  </div>
                </div>
              </div>
              <div className="w-full flex flex-col bg-white rounded-3xl p-[30px] gap-[30px] max-w-[1200px]">
                <div className="flex items-center text-base text-gray-600 font-light gap-x-[10px]">
                  <img src="assets/orgData.png" className="w-[20px]" />
                  <div className="uppercase">
                    {t("profile_card.organization_title")}
                  </div>
                </div>
                <div className="flex gap-x-[100px] gap-y-[20px] items-center text-base flex-wrap">
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.organization_name")}
                    <p className="text-gray-600 font-light">
                      {userData.organizationName}
                    </p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.organization_iin_bin")}

                    <p className="text-gray-600 font-light">{userData.iinBin}</p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.organization_address")}

                    <p className="text-gray-600 font-light">
                      {`${userData.address.city || ""}${userData.address.city ? "," : ""} 
    ${userData.address.street || ""}${userData.address.street ? "," : ""} 
    ${userData.address.house || ""} 
    ${userData.address.building ? userData.address.building : ""}
    ${userData.address.premises ? userData.address.premises : ""}`}
                    </p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.organization_website")}

                    <p className="font-light">
                      {userData.organizationWebsite
                        ? userData.organizationWebsite
                        : "-"}
                    </p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.organization_phone")}

                    <p className="font-light">{userData.organizationPhone}</p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.organization_email")}

                    <p className="font-light">{userData.organizationEmail}</p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.code_ogd")}
                    {userData.address.refOgd?.id &&
                      userData.address.refOgd?.name ? (
                      <p className="font-light">
                        {userData.address.refOgd?.name}
                      </p>
                    ) : (
                      <p className="font-light">-</p>
                    )}
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.organization_data")}

                    <p className="font-light">
                      {userData.organizationCreatedAt
                        ? format(new Date(userData.organizationCreatedAt), "dd.MM.yyyy, HH:mm")
                        : "Дата не указана"}
                    </p>
                  </div>
                </div>

              </div>

              <div className="w-full flex flex-col bg-white rounded-3xl p-[30px] items-stretch gap-[30px] max-w-[1200px] max-h-[230px]">
                <div className="flex items-center text-base text-gray-600 font-light gap-x-[10px]">
                  <img src="assets/docs.png" className="w-[20px]" />
                  <div className="uppercase">
                    {t("profile_card.participation_in_the_system")}
                  </div>
                </div>

                <div className="flex gap-x-[100px] gap-y-[20px]  text-base flex-wrap">
                  <div className="flex flex-col gap-[20px]">
                    <div className="text-gray-600 w-[300px] font-semibold">
                      <div className={style.tooltipWrapper}>
                        {t('profile_card.registration_date')}
                        <FaInfoCircle className={style.errorIcon} />
                        <div className={style.tooltipText}>
                          {t('profile_card.registration_date_tooltip')}
                        </div>
                      </div>
                      {userData.createdAt ? (
                        <p className="text-gray-600 font-light">
                          {format(new Date(userData?.createdAt), 'dd.MM.yyyy')}
                        </p>
                      ) : (
                        '-'
                      )}

                    </div>
                    <div className="text-gray-600 w-[300px] font-semibold">
                      {t("profile_card.organization_product_group")}

                      <p className="font-light">
                        {userData.refProductGroup.name
                          ? userData.refProductGroup.name
                          : "-"}
                      </p>
                    </div>
                  </div>
                  <div className="text-gray-600 max-w-[600px] w-full font-semibold max-h-[100px]">
                    {t("profile_card.role")}

                    <div className="text-gray-600 font-light grid grid-cols-2 gap-4">
                      {userData?.selectRoleV2?.length > 0 ? (
                        userData.selectRoleV2.slice(0, 4).map((role: any, index: any) => (
                          <p key={index} className="block">
                            {role.nameRu}
                          </p>
                        ))
                      ) : (
                        <p>-</p>
                      )}
                    </div>
                  </div>

                </div>
              </div>

              <div className="w-full flex flex-col bg-white rounded-3xl p-[30px] items-stretch gap-[30px] max-w-[1200px]">
                <div className="flex items-center text-base text-gray-600 font-light gap-x-[10px]">
                  <img src="assets/docs.png" className="w-[20px]" />
                  <div className="uppercase">{t("profile_card.bank_title")}</div>
                </div>
                <div className="flex gap-x-[100px] gap-y-[20px] items-center text-base flex-wrap">
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.bank_name")}
                    <p className="text-gray-600 font-light">
                      {userData.refBankCatalog.name
                        ? userData.refBankCatalog.name
                        : "-"}
                    </p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.bank_identification_code")}
                    <p className="text-gray-600 font-light">
                      {userData.bankIdentificationCode}
                    </p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.bank_checking_account")}
                    <p className="text-gray-600 font-light">
                      {userData.checkingAccount}
                    </p>
                  </div>
                  <div className="text-gray-600 w-[300px] font-semibold">
                    {t("profile_card.bank_kbe")}
                    <p className="font-light">
                      {userData.kbe ? userData.kbe : "-"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pb-[2px]"></div>
            </>
          )}
          {page == 2 && accountsLoading && (
            <div className="flex justify-center">
              <Loader />
            </div>
          )}
          {page == 2 && !accountsLoading && activeAccounts.length === 0 && (
            <div className="w-full max-w-[1200px]">
              <EmptyListPlaceholderContent
                title={t("accountDetails.noActiveAccounts")}
              />
            </div>
          )}
          {page == 2 && !accountsLoading && activeAccounts.length > 0 && (
            <div className="w-full max-w-[1200px]">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {activeAccounts.map((account) => {
                  const isRefreshing = refreshingAccountId === account.accountId;
                  const groupName = getProductGroupDisplayName(
                    {
                      code: account.productGroupCode,
                      name: account.productGroupName,
                    },
                    locale,
                  );

                  return (
                    <div
                      key={account.accountId}
                      className="flex flex-col gap-4 rounded-xl border border-gray-200 shadow-md p-5 bg-white"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-base font-semibold text-gray-900">
                          {groupName || account.productGroupName || "-"}
                        </span>
                        <StatusBadge type="active">
                          {t("productGroupSign.statusActive")}
                        </StatusBadge>
                      </div>

                      <div className="border-t border-gray-100 pt-3 flex flex-col">
                        <div className="flex flex-row gap-[6px] items-center flex-wrap">
                          <label className="font-light text-gray-600 text-sm">
                            {t("accountDetails.balance")}
                          </label>
                          {account.updatedAt && (
                            <div className="text-[10px] font-light bg-[#e9f0f3] rounded-[3px] px-[4px] py-[1px] text-[#1e2930]">
                              {locale == "ru"
                                ? `на ${account.updatedAt}`
                                : `${account.updatedAt} уақытына`}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-[10px] mt-1">
                          <span className="text-xl font-semibold text-gray-900">
                            {formatBalance(account.currentBalance)} тг.
                          </span>
                          <button
                            type="button"
                            className="flex items-center justify-center disabled:opacity-40"
                            disabled={isRefreshing}
                            title={t("mainButtons.refresh")}
                            onClick={() => handleRefreshAccount(account.accountId)}
                          >
                            <img
                              src="/icons/update.svg"
                              alt=""
                              className={isRefreshing ? "animate-spin" : ""}
                            />
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col">
                          <label className="font-light text-gray-600 text-sm">
                            {t("accountDetails.personalAccountNumber")}
                          </label>
                          <span className="text-sm text-gray-900 break-all">
                            {account.accountNumber || "-"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <label className="font-light text-gray-600 text-sm">
                            {t("accountDetails.documentId")}
                          </label>
                          <span className="text-sm text-gray-900 break-all">
                            {account.eoContractNumber || "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {page === 4 && isLoading && (
            <div className="flex justify-center">
              <Loader />
            </div>
          )}
          {page === 4 && !isLoading && (
            <div className="w-full mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {groups.map((group, idx) => {
                  const isActive = checked[idx] || false;
                  const groupDocs = isActive ? getGroupDocs(group.code) : [];

                  return (
                    <div
                      key={group.id}
                      className="flex flex-col gap-3 rounded-xl border border-gray-200 shadow-md p-4 bg-white"
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {getProductGroupDisplayName(group, locale)}
                      </span>
                      <div className="flex items-center justify-between gap-2">
                        <StatusBadge type={isActive ? "active" : undefined}>
                          {isActive
                            ? t("productGroupSign.statusActive")
                            : t("productGroupSign.statusInactive")}
                        </StatusBadge>
                        {!isActive && (
                          <button
                            type="button"
                            className="bg-green text-white text-sm font-semibold py-1.5 px-3 rounded"
                            onClick={() => handleSubscribeClick(group)}
                          >
                            {t("productGroupSign.subscribe")}
                          </button>
                        )}
                      </div>
                      {isActive && groupDocs.length > 0 && (
                        <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-2">
                          {groupDocs.map((doc: any) => (
                            <button
                              key={doc.guidMinio}
                              type="button"
                              className="flex items-start gap-2 text-left text-xs text-green hover:text-green/80 transition-colors"
                              onClick={() => handleOpenGroupDoc(doc.guidMinio)}
                            >
                              <img
                                src="/icons/download.svg"
                                alt=""
                                className="w-3.5 h-3.5 mt-0.5 shrink-0"
                              />
                              <span
                                className="leading-tight line-clamp-2"
                                title={localizeProductGroupFileName(
                                  doc.fileName,
                                  group.code,
                                  locale,
                                )}
                              >
                                {localizeProductGroupFileName(
                                  doc.fileName,
                                  group.code,
                                  locale,
                                )}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {page == 3 && (
            <>
              <div className="rounded-lg bg-white overflow-auto max-w-[1200px]">
                <table className="max-w-full w-full leading-normal">
                  <thead className="border-b-2 border-gray-300">
                    <tr>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                        scope="col"
                      >
                        №{t("documents_orders.number")}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                        scope="col"
                      >
                        {t('profile_card.name')}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200 "
                        scope="col"
                      >
                        {t('profile_card.iin')}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200 "
                        scope="col"
                      >
                        {t('profile_card.postion')}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                        scope="col"
                      >
                        {t('profile_card.registration_date')}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                        scope="col"
                      >
                        {t('profile_card.phone')}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                        scope="col"
                      >
                        {t('profile_card.email')}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                        scope="col"
                      >
                        {t('documentLabels.status')}
                      </th>
                      <th
                        className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                        scope="col"
                      >
                        {t('profile_card.role')}
                      </th>

                    </tr>
                  </thead>
                  {isLoading ? (
                    <SkeletonTheme baseColor="#ebebeb" highlightColor="#c9c9c9">
                      <tbody className="font-normal text-center">
                        {new Array(10).fill(1).map((_, index) => (
                          <MockSkeleton index={index} />
                        ))}
                      </tbody>
                    </SkeletonTheme>
                  ) : tableData?.values?.length === 0 || !tableData?.values ? (
                    <tbody>
                      <tr>
                        <td colSpan={8} className="text-center py-5">
                          <div>
                            <EmptyListPlaceholderContent
                              title={t("Сотрудники отсутствуют")}
                            // description={t("orderList.noOrdersDescription")}
                            />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody className="font-normal text-center">
                      {tableData?.values?.map((value: any, index: any) => (
                        <tr
                          key={`tr-${index}-${value.id}`}
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => {
                            console.log('navigateTo', ("/cheque/" + value?.id));
                            // {userData.selectRole[0] == "Оператор (администратор ЦРЦЭ)" ? navigate("/cheque/" + value?.id):
                            //   value.status == 'PROCESSED' ?  navigate("/cheque/" + value?.id): {}}
                            navigate("/cheque/" + value?.id)
                          }
                          }

                        >
                          <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                            {calculateItemNumber(index) || "-"}
                          </td>
                          <td className="px-5 py-5 text-sm  border-b border-gray-200 text-gray-600 cursor-pointer "
                          >
                            {value?.fiscalDocumentNumber || "-"}
                          </td>
                          <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                            {value?.kktRegId || "-"}
                          </td>
                          <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600 text-center w-[225px]">
                            {/* {format(value?.dateTime, "dd.MM.yyyy") || '-'} */}
                            {value.dateTime || ''}
                          </td>
                          <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600 text-center">

                            {value?.operationType == '1' ? "Чек продажи" : 'Чек возврата'}
                          </td>
                          <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600 text-center">
                            <div className="flex justify-center">
                              <StatusBadge
                                type={statusTypes[value.status]?.type || "unsaved"}
                                fontSize="15px"
                                circleSize="8px"
                              >
                                {statusTypes[value.status]?.label ||
                                  t("documents_upload.unknownStatus")}
                              </StatusBadge>
                            </div>

                          </td>
                          {value.status == 'PROCESSED' ? (
                            <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600"
                              onClick={(e) => e.stopPropagation()}
                            >

                            </td>
                          ) : (<td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600"
                            onClick={(e) => e.stopPropagation()}
                          >
                          </td>)}
                        </tr>
                      ))}
                    </tbody>
                  )}
                </table>
              </div>
            </>
          )}
          <ProductGroupSignModal
            isOpen={!!pgModalFiles}
            files={pgModalFiles}
            productGroups={pgModalGroup ? [pgModalGroup] : groups}
            signing={pgSubmitting}
            title={
              pgModalGroup
                ? `${t("productGroupSign.modalTitle")} — ${getProductGroupDisplayName(pgModalGroup, locale)}`
                : t("productGroupSign.modalTitle")
            }
            onClose={closePgModal}
            onSign={handleSignAndConnect}
          />
          <ModalComponent
            isOpen={errorModal}
            onClose={() => {
              setErrorModal(null);
            }}
            headerContent={
              <h1 className="h__modal">
                {t("documentNotification.notification")}
              </h1>
            }
            content={
              <div className="error-list">
                <ul>
                  {Array.isArray(errorModal) && errorModal.length > 0 ? (
                    errorModal.map((error: any, index: number) => {
                      const errorText = typeof error === "object" ? JSON.stringify(error) : error;
                      return (
                        <li className=" border-gray-300 py-1" key={index}>
                          {t(`error_dictionary.${errorText}`)}
                        </li>
                      );
                    })
                  ) : typeof errorModal === "object" && errorModal !== null ? (
                    errorModal.errorMessage ? (
                      <li className="border-b border-gray-300 py-1">
                        {t('documentStatus.error')}: {errorModal.errorMessage}
                      </li>
                    ) : (
                      <li>{t("documentNotification.no_errors")}</li>
                    )
                  ) : typeof errorModal === "string" ? (
                    <li className=" border-gray-300 py-1">
                      {errorModal}
                    </li>
                  ) : (
                    <li>{t("documentNotification.no_errors")}</li>
                  )}
                </ul>
              </div>
            }
            customContent={
              <ModalButton
                type="button"
                onClick={() => {
                  setErrorModal(null);
                }}
              >
                {t("mainButtons.accept")}
              </ModalButton>
            }
          />
        </div>

      )}

    </Formik>
  );
}

export default ProfileCard;
